/**
 * 🔒 An article's callouts (piece 30, Article Editor; T3572; decision 10530):
 * a block of one of three kinds, Note, Warning and Tip, stored as one `aside`
 * carrying `data-kb-callout`, whose value the article's allowed table checks
 * against the three words, as it checks a picture's `data-kb-caption`.
 *
 * 🔨 A callout holds paragraphs, lists, marked words, links and line breaks,
 * and never another callout, a heading, a quotation, a code block, a table, a
 * picture, a rule or the table of contents. The glyph and the word it opens on
 * are drawn by the stylesheet, on screen and on paper, and never stored.
 *
 * This file imports nothing but the escape, so the sanitiser, the import and
 * the two renderers each read it without a cycle.
 */

/** The three kinds, in the order the style menu offers them. */
export const ARTICLE_CALLOUT_KINDS = ['note', 'warning', 'tip'] as const;
export type ArticleCalloutKind = (typeof ARTICLE_CALLOUT_KINDS)[number];

/** The attribute a callout's kind is stored under. */
export const ARTICLE_CALLOUT_ATTRIBUTE = 'data-kb-callout';

export function isArticleCalloutKind(value: unknown): value is ArticleCalloutKind {
  return typeof value === 'string' && (ARTICLE_CALLOUT_KINDS as readonly string[]).includes(value);
}

/** The kind a stored or typed value names, read as the table keeps it, or null for anything else. */
export function articleCalloutKindOf(value: string): ArticleCalloutKind | null {
  const kind = value.trim().toLowerCase();
  return isArticleCalloutKind(kind) ? kind : null;
}

/** The lookup key of a kind's word: the style menu's entry and the word the callout opens on. */
export function articleCalloutWordKey(kind: ArticleCalloutKind): `kb.callout.${ArticleCalloutKind}` {
  return `kb.callout.${kind}`;
}

/**
 * The glyph each kind opens on: Lucide's own `info`, `triangle-alert` and
 * `lightbulb` outlines (ISC licence), drawn as a mask the stylesheet paints in
 * the callout's rule colour, so the one drawing reads on screen and on paper
 * and carries no colour of its own.
 */
const GLYPH_SHAPES: Readonly<Record<ArticleCalloutKind, string>> = {
  note: "<circle cx='12' cy='12' r='10'/><path d='M12 16v-4'/><path d='M12 8h.01'/>",
  warning: "<path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3'/><path d='M12 9v4'/><path d='M12 17h.01'/>",
  tip: "<path d='M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5'/><path d='M9 18h6'/><path d='M10 22h4'/>",
};

/** A kind's glyph as a CSS image, for a `mask-image`: an outline stroked in black, which a mask reads as its shape alone. */
export function articleCalloutGlyphUrl(kind: ArticleCalloutKind): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>${GLYPH_SHAPES[kind]}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

/** A word as a CSS string, for a `content` the stylesheet draws: its quote and its backslash escaped. */
export function cssString(words: string): string {
  return `"${words.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, ' ')}"`;
}

/** The elements a callout never holds (decision 10530): any of them inside one takes its element away. */
const NOT_IN_CALLOUT = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'table', 'img', 'hr', 'nav']);
const CALLOUT_TOKEN = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>/g;
const KIND = /\bdata-kb-callout="([^"]*)"/i;

interface OpenCallout {
  start: number;
  end: number;
  keep: boolean;
}

/**
 * 🔨 The callouts the stored body keeps (decision 10530), run after the allowed
 * table: an `aside` naming none of the three kinds, a callout inside a callout,
 * a callout holding anything but paragraphs, lists and marked words, and an
 * `aside` left open or closed with none open each lose their element. Their
 * words stand where they stood, so nothing a person wrote is dropped.
 */
export function articleCalloutsKept(html: string): string {
  if (!/<\/?aside\b/i.test(html)) return html;
  const cut: [number, number][] = [];
  const open: OpenCallout[] = [];
  for (const match of html.matchAll(CALLOUT_TOKEN)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    const name = match[2]!.toLowerCase();
    const closing = match[1] === '/';
    if (name === 'aside') {
      if (!closing) {
        const kind = articleCalloutKindOf(KIND.exec(match[3] ?? '')?.[1] ?? '');
        open.push({ start, end, keep: kind !== null && open.length === 0 });
        continue;
      }
      const opened = open.pop();
      if (!opened) {
        cut.push([start, end]);
        continue;
      }
      if (!opened.keep) cut.push([opened.start, opened.end], [start, end]);
      continue;
    }
    if (!closing && NOT_IN_CALLOUT.has(name) && open[0]) open[0].keep = false;
  }
  for (const left of open) cut.push([left.start, left.end]);
  if (cut.length === 0) return html;
  cut.sort((a, b) => a[0] - b[0]);
  let out = '';
  let at = 0;
  for (const [start, end] of cut) {
    out += html.slice(at, start);
    at = end;
  }
  return out + html.slice(at);
}

/** A callout in its one stored form, around markup already safe. */
export function articleCalloutHtml(kind: ArticleCalloutKind, inner: string): string {
  return `<aside ${ARTICLE_CALLOUT_ATTRIBUTE}="${kind}">${inner}</aside>`;
}
